import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import getShareholderTransactionsService, {
  getCoveredWarrantsService,
  getExerciseRightsService,
  getOtherInformationService,
  getIssuanceMaturityService,
} from 'services/analyticsCenter/businessNews';
import {
  CoveredWarrantsDataTypes,
  GetCoveredWarrantsParamsTypes,
  ShareholderTransactionsParamsTypes,
  ShareholderTransactionsTypes,
  OtherInformationParamsTypes,
  OtherInformationTypes,
  ExerciseRightDataTypes,
  ExerciseRightParamsTypes,
  IssuanceMaturityParamsTypes,
} from 'services/analyticsCenter/businessNews/types';
import { SGDCKNewsDetailDataTypes } from 'services/news/newsTransBasisDetail/type';
import getWsPriceboardService from 'services/wsPriceboard';
import { parseStringToNumber } from 'utils/functions';

type BusinessNewsState = {
  otherInformation?: PaginationData<OtherInformationTypes[]>;
  shareholderTransactions?: PaginationData<ShareholderTransactionsTypes[]>;
  coveredWarrants: CoveredWarrantsDataTypes[];
  exerciseRight?: PaginationData<ExerciseRightDataTypes[]>;
  issuanceMaturity?: PaginationData<SGDCKNewsDetailDataTypes[]>;
  issuanceMaturityLastItemsLength?: number;
};

const initialState: BusinessNewsState = {
  otherInformation: undefined,
  shareholderTransactions: undefined,
  coveredWarrants: [],
  exerciseRight: undefined,
  issuanceMaturity: undefined,
  issuanceMaturityLastItemsLength: 0,
};

export const getOtherInformationAction = createAsyncThunk<
{ data: PaginationData<OtherInformationTypes[]>, isFirst?: boolean },
  OtherInformationParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessNewsReducer/getOtherInformation',
  async (params, { rejectWithValue }) => {
    try {
      const data = await getOtherInformationService({
        stock_symbol: params.stock_symbol,
        page: params.page,
        limit: params.limit,
      });
      return { data, isFirst: params.isFirst };
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getShareholderTransactionsAction = createAsyncThunk<
{ data: PaginationData<ShareholderTransactionsTypes[]>, isFirst?: boolean },
  ShareholderTransactionsParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessNewsReducer/getShareholderTransactionsAction',
  async (params, { rejectWithValue }) => {
    try {
      const data = await getShareholderTransactionsService({
        stock_symbol: params.stock_symbol,
        announced_date: params.announced_date,
        page: params.page,
        limit: params.limit,
      });
      return { data, isFirst: params.isFirst };
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getCoveredWarrantsAction = createAsyncThunk<
  CoveredWarrantsDataTypes[],
  GetCoveredWarrantsParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessNewsReducer/getCoveredWarrantsAction',
  async (params: GetCoveredWarrantsParamsTypes, { rejectWithValue }) => {
    try {
      return await getCoveredWarrantsService(params);
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getPriceboardBussinessNewAction = createAsyncThunk<
  SocketResponseTypes,
  string,
  { rejectValue: ErrorResponse }
>(
  'stockInformationReducer/getPriceboardStockInfoAction',
  async (stockCode, { rejectWithValue }) => {
    try {
      const listCovered = await getCoveredWarrantsService({ stock_symbol: stockCode });
      return await getWsPriceboardService({
        selectedStocks: listCovered.map((item) => item.contractCode).toString(),
        criteriaId: '',
        marketId: 0,
        lastSeq: 0,
        isReqTL: false,
        isReqMK: false,
        tlSymbol: stockCode,
        pthMktId: '',
        isGetNullValue: false,
        isIncrValue: true,
      });
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getExerciseRightAction = createAsyncThunk<
{ data: PaginationData<ExerciseRightDataTypes[]>, isFirst?: boolean },
  ExerciseRightParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessNewsReducer/getExerciseRightAction',
  async (params, { rejectWithValue }) => {
    try {
      const data = await getExerciseRightsService({
        stock_symbol: params.stock_symbol,
        page: params.page,
        limit: params.limit,
        keyword: params.keyword,
        exercise_right_type_id: params.exercise_right_type_id,
      });
      return { data, isFirst: params.isFirst };
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const getIssuanceMaturityAction = createAsyncThunk<
  { data: PaginationData<SGDCKNewsDetailDataTypes[]>, isFirst?: boolean },
  IssuanceMaturityParamsTypes,
  { rejectValue: ErrorResponse }
>(
  'businessNewsReducer/getIssuanceMaturityAction',
  async (params, { rejectWithValue }) => {
    try {
      const data = await getIssuanceMaturityService({
        keyword: params.keyword,
        page: params.page,
        limit: params.limit,
        offset: params.offset,
        category_slug: params.category_slug,
        stock_symbol: params.stock_symbol,
      });
      return { data, isFirst: params.isFirst };
    } catch (error) {
      return rejectWithValue(error as ErrorResponse);
    }
  },
);

export const businessNewsSlice = createSlice({
  name: 'businessNewsReducer',
  initialState,
  reducers: {
    updateCoveredWarrants: ($state, action: PayloadAction<StockInfoTypes[]>) => {
      if ($state.coveredWarrants.length) {
        const cloneData = $state.coveredWarrants.slice();
        for (let i = 0; i < action.payload.length; i += 1) {
          const element = action.payload[i];
          const index = cloneData.findIndex((item) => item.contractCode === element.A);
          if (index >= 0) {
            cloneData[index] = {
              ...cloneData[index],
              ceiling: parseStringToNumber(element.CL),
              floor: parseStringToNumber(element.FL),
              total: parseStringToNumber(element.RE),
            };
          }
        }
        $state.coveredWarrants = cloneData;
      }
    },
  },
  extraReducers(builder) {
    builder.addCase(getOtherInformationAction.fulfilled, ($state, action) => {
      $state.otherInformation = !$state.otherInformation || action.payload.isFirst
        ? action.payload.data : {
          ...action.payload.data,
          data: [...$state.otherInformation.data, ...action.payload.data.data],
        };
    });
    builder.addCase(
      getShareholderTransactionsAction.fulfilled,
      ($state, action) => {
        $state.shareholderTransactions = !$state.shareholderTransactions || action.payload.isFirst
          ? action.payload.data : {
            ...action.payload.data,
            data: [...$state.shareholderTransactions.data, ...action.payload.data.data],
          };
      },
    );
    builder.addCase(getCoveredWarrantsAction.fulfilled, ($state, action) => {
      $state.coveredWarrants = action.payload;
    });
    builder.addCase(getExerciseRightAction.fulfilled, ($state, action) => {
      $state.exerciseRight = !$state.exerciseRight || action.payload.isFirst
        ? action.payload.data : {
          ...action.payload.data,
          data: [...$state.exerciseRight.data, ...action.payload.data.data],
        };
    });
    builder.addCase(getIssuanceMaturityAction.fulfilled, ($state, action) => {
      $state.issuanceMaturity = !$state.issuanceMaturity || action.payload.isFirst
        ? action.payload.data : {
          ...action.payload.data,
          data: [...$state.issuanceMaturity.data, ...action.payload.data.data],
        };
      $state.issuanceMaturityLastItemsLength = action.payload.data.data.length || 0;
    });

    builder.addCase(getPriceboardBussinessNewAction.fulfilled, ($state, action) => {
      if (action.payload.pb && action.payload.pb.f && action.payload.pb.f.length) {
        $state.coveredWarrants = action.payload.pb.f.map((item) => {
          const stock = JSON.parse(item) as StockInfoTypes;
          const td = stock.TD.split('/');
          return {
            contractCode: stock.A,
            expirationDate: `${td[2]}/${td[1]}/${td[0]}`,
            ceiling: parseStringToNumber(stock.CL),
            floor: parseStringToNumber(stock.FL),
            total: parseStringToNumber(stock.RE),
          };
        });
      } else {
        $state.coveredWarrants = [];
      }
    });
  },
});

export const { updateCoveredWarrants } = businessNewsSlice.actions;

export default businessNewsSlice.reducer;
